import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Tab,
  Tabs,
  Box,
  Typography,
  FormControlLabel,
  Switch,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import { i18n } from "../../translate/i18n";
import toastUtils from "../../utils/toastUtils";
import {
  createHelpArticle,
  updateHelpArticle,
  getHelpArticle,
} from "../../services/helpApi";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    minHeight: "80vh",
    maxHeight: "80vh",
  },
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(2),
  },
  textarea: {
    fontFamily: "monospace",
    fontSize: "14px",
  },
}));

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const HelpArticleModal = ({ open, onClose, articleId, categories }) => {
  const classes = useStyles();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    categoryId: "",
    slug: "",
    titlePt: "",
    titleEn: "",
    titleEs: "",
    descriptionPt: "",
    descriptionEn: "",
    descriptionEs: "",
    contentPt: "",
    contentEn: "",
    contentEs: "",
    status: "draft",
    order: 0,
    tags: "[]",
    relatedArticles: "[]",
    videoUrl: "",
    images: "[]",
  });

  useEffect(() => {
    if (articleId && open) {
      loadArticle();
    } else if (!articleId && open) {
      resetForm();
    }
  }, [articleId, open]);

  const loadArticle = async () => {
    try {
      setLoading(true);
      const { data } = await getHelpArticle(articleId);
      setFormData({
        categoryId: data.categoryId || "",
        slug: data.slug || "",
        titlePt: data.titlePt || "",
        titleEn: data.titleEn || "",
        titleEs: data.titleEs || "",
        descriptionPt: data.descriptionPt || "",
        descriptionEn: data.descriptionEn || "",
        descriptionEs: data.descriptionEs || "",
        contentPt: data.contentPt || "",
        contentEn: data.contentEn || "",
        contentEs: data.contentEs || "",
        status: data.status || "draft",
        order: data.order || 0,
        tags: data.tags || "[]",
        relatedArticles: data.relatedArticles || "[]",
        videoUrl: data.videoUrl || "",
        images: data.images || "[]",
      });
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      categoryId: "",
      slug: "",
      titlePt: "",
      titleEn: "",
      titleEs: "",
      descriptionPt: "",
      descriptionEn: "",
      descriptionEs: "",
      contentPt: "",
      contentEn: "",
      contentEs: "",
      status: "draft",
      order: 0,
      tags: "[]",
      relatedArticles: "[]",
      videoUrl: "",
      images: "[]",
    });
    setActiveTab(0);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const generateSlug = (text) => {
    return text
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/--+/g, "-")
      .trim();
  };

  const handleTitleChange = (e) => {
    const { value } = e.target;
    setFormData((prev) => ({
      ...prev,
      titlePt: value,
      slug: generateSlug(value),
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      if (!formData.categoryId || !formData.slug || !formData.titlePt || !formData.contentPt) {
        toastUtils.error("Preencha todos os campos obrigatórios");
        return;
      }

      if (articleId) {
        await updateHelpArticle(articleId, formData);
        toastUtils.success(i18n.t("helpAdmin.articleUpdated"));
      } else {
        await createHelpArticle(formData);
        toastUtils.success(i18n.t("helpAdmin.articleCreated"));
      }

      handleClose();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle>
        {articleId
          ? i18n.t("helpAdmin.editArticle")
          : i18n.t("helpAdmin.newArticle")}
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth margin="dense">
              <InputLabel>{i18n.t("helpAdmin.form.category")} *</InputLabel>
              <Select
                name="categoryId"
                value={formData.categoryId}
                onChange={handleInputChange}
              >
                {categories
                  .filter((c) => !c.parentId)
                  .map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.namePt}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              margin="dense"
              label={i18n.t("helpAdmin.form.slug")}
              name="slug"
              value={formData.slug}
              onChange={handleInputChange}
              helperText="URL amigável (gerado automaticamente)"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth margin="dense">
              <InputLabel>{i18n.t("helpAdmin.form.status")}</InputLabel>
              <Select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
              >
                <MenuItem value="draft">Rascunho</MenuItem>
                <MenuItem value="published">Publicado</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              margin="dense"
              type="number"
              label={i18n.t("helpAdmin.form.order")}
              name="order"
              value={formData.order}
              onChange={handleInputChange}
              helperText="Ordem de exibição (0 = primeiro)"
            />
          </Grid>
        </Grid>

        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          className={classes.tabs}
        >
          <Tab label="🇧🇷 Português" />
          <Tab label="🇺🇸 English" />
          <Tab label="🇪🇸 Español" />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          <TextField
            fullWidth
            margin="dense"
            label="Título *"
            name="titlePt"
            value={formData.titlePt}
            onChange={handleTitleChange}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Descrição"
            name="descriptionPt"
            value={formData.descriptionPt}
            onChange={handleInputChange}
            multiline
            rows={2}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Conteúdo (Markdown) *"
            name="contentPt"
            value={formData.contentPt}
            onChange={handleInputChange}
            multiline
            rows={15}
            className={classes.textarea}
            helperText="Use Markdown para formatação"
          />
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <TextField
            fullWidth
            margin="dense"
            label="Title"
            name="titleEn"
            value={formData.titleEn}
            onChange={handleInputChange}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Description"
            name="descriptionEn"
            value={formData.descriptionEn}
            onChange={handleInputChange}
            multiline
            rows={2}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Content (Markdown)"
            name="contentEn"
            value={formData.contentEn}
            onChange={handleInputChange}
            multiline
            rows={15}
            className={classes.textarea}
            helperText="Use Markdown for formatting"
          />
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <TextField
            fullWidth
            margin="dense"
            label="Título"
            name="titleEs"
            value={formData.titleEs}
            onChange={handleInputChange}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Descripción"
            name="descriptionEs"
            value={formData.descriptionEs}
            onChange={handleInputChange}
            multiline
            rows={2}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Contenido (Markdown)"
            name="contentEs"
            value={formData.contentEs}
            onChange={handleInputChange}
            multiline
            rows={15}
            className={classes.textarea}
            helperText="Use Markdown para formato"
          />
        </TabPanel>

        <Box mt={2}>
          <Typography variant="caption" color="textSecondary">
            * Campos obrigatórios
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="default">
          {i18n.t("buttons.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={loading}
        >
          {i18n.t("buttons.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HelpArticleModal;
